import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { MonthlyOverview } from '../components/sections/MonthlyOverview';
import { QuickStats } from '../components/sections/QuickStats';
import { UpcomingPayments } from '../components/sections/UpcomingPayments';
import { SubscriptionsList } from '../components/sections/SubscriptionsList';
import { subscriptionService } from '../services/subscriptionService';
import { SpendingTrendChart } from '../components/charts/SpendingTrendChart';
import { CategoryPieChart } from '../components/charts/CategoryPieChart';
import { useAuth } from '../context/AuthContext';
import { convertCurrency, getCurrencySymbol } from '../config/currencies';

export const DashboardPage = () => {
    const navigate = useNavigate();
    const [subscriptions, setSubscriptions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [monthlyData, setMonthlyData] = useState([]);
    const [categoryData, setCategoryData] = useState([]);
    const { user } = useAuth();
    const [userCurrency, setUserCurrency] = useState('USD');

    useEffect(() => {
        // Update currency whenever user preferences change
        setUserCurrency(user?.preferences?.currency || 'USD');
    }, [user?.preferences?.currency]);

    const currencySymbol = getCurrencySymbol(userCurrency);

    useEffect(() => {
        const initializeDashboard = async () => {
            await fetchSubscriptions();
            calculateMonthlyTrends();
        };

        initializeDashboard();
    }, []);

    const fetchSubscriptions = async () => {
        try {
            setIsLoading(true);
            const data = await subscriptionService.getAllSubscriptions();
            setSubscriptions(data);

            if (data.length) {
                const categories = data.reduce((acc, sub) => {
                    const category = sub.category || 'Uncategorized';
                    acc[category] = (acc[category] || 0) + sub.cost;
                    return acc;
                }, {});

                const categoryDataArray = Object.entries(categories).map(([category, amount]) => ({
                    category,
                    amount
                }));

                setCategoryData(categoryDataArray);
            }

            setError(null);
        } catch (err) {
            if (err.status === 401) {
                localStorage.removeItem('auth_token');
                localStorage.removeItem('auth_user');
                navigate('/login?redirect=' + encodeURIComponent(window.location.pathname));
                return;
            }

            setError(err.userMessage || 'Failed to fetch subscriptions. Please try again later.');
            console.error('Error fetching subscriptions:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const calculateMonthlyTrends = () => {
        const last6Months = Array.from({ length: 6 }, (_, i) => {
            const date = new Date();
            date.setMonth(date.getMonth() - i);
            return {
                month: date.toLocaleString('default', { month: 'short' }),
                amount: Math.random() * 1000 // Replace with actual data
            };
        }).reverse();

        setMonthlyData(last6Months);
    };

    const calculateMonthlyCost = (subscription) => {
        const { cost, subscription_type, currency } = subscription;
        // First convert to monthly rate
        const monthlyBase = (() => {
            switch (subscription_type?.toLowerCase()) {
                case 'daily':
                    return cost * 30;
                case 'weekly':
                    return cost * 4.33;
                case 'yearly':
                    return cost / 12;
                case 'monthly':
                default:
                    return cost;
            }
        })();

        // Then convert to user's preferred currency
        return convertCurrency(monthlyBase, currency || 'USD', userCurrency);
    };

    const totalMonthlySpent = subscriptions
        .filter(sub => sub.status === 'active')
        .reduce((sum, sub) => sum + calculateMonthlyCost(sub), 0);

    if (isLoading) {
        return (
            <MainLayout>
                <div className="space-y-6">
                    <MonthlyOverview totalSpent={0} budget={user?.preferences?.budget || 0} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <QuickStats subscriptions={[]} totalSpent={0} />
                        <UpcomingPayments subscriptions={[]} />
                    </div>
                    <SubscriptionsList
                        subscriptions={[]}
                        onSubscriptionUpdate={fetchSubscriptions}
                        isLoading={true}
                    />
                </div>
            </MainLayout>
        );
    }

    if (error) {
        return (
            <MainLayout>
                <div className="text-center text-red-600 p-4">
                    {error}
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="space-y-6">
                <MonthlyOverview
                    totalSpent={totalMonthlySpent}
                    budget={user?.preferences?.budget || 0}
                    currency={userCurrency}
                    currencySymbol={currencySymbol}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <QuickStats
                        subscriptions={subscriptions}
                        totalSpent={totalMonthlySpent}
                        currency={userCurrency}
                        currencySymbol={currencySymbol}
                    />
                    <UpcomingPayments
                        subscriptions={subscriptions}
                        currency={userCurrency}
                        currencySymbol={currencySymbol}
                    />
                </div>

                <SubscriptionsList
                    subscriptions={subscriptions}
                    onSubscriptionUpdate={fetchSubscriptions}
                    isLoading={isLoading}
                    limit={5}
                    currency={userCurrency}
                    currencySymbol={currencySymbol}
                />
            </div>
        </MainLayout>
    );
};